import _ from 'lodash';
import { get } from 'lodash';
import {
    PERMISSION_BACKSTAGE_ITEMS,
    PERMISSION_MANAGER_ITEMS,
    PERMISSION_PAYMENT_ITEMS,
} from './permission-settings-config';
import { PERMISSION_ALL_KEYS } from './permission-settings-quick-presets';
import {
    formPermissionRowToApiSnapshot,
    normalizePermissionBitForCompare,
    normalizeRowForForm,
} from './permission-normalize.utils';
import type { EmployeePermissionClassRow } from './permission-settings.types';
import type { JobSettingsExceptionItem, JobSettingsExceptionKind } from './job-settings.types';

const PERMISSION_DEF_BY_KEY = new Map<string, { key: string; labelKey: string }>(
    [...PERMISSION_PAYMENT_ITEMS, ...PERMISSION_MANAGER_ITEMS, ...PERMISSION_BACKSTAGE_ITEMS].map((d) => [
        d.key,
        d,
    ]),
);

/** 解析接口 `permission_override` 字符串为表单态；无效则 `null` */
export function parsePermissionOverrideToForm(raw: unknown): EmployeePermissionClassRow | null {
    if (raw == null) {
        return null;
    }
    const s = typeof raw === 'string' ? raw.trim() : '';
    if (s === '' && typeof raw !== 'object') {
        return null;
    }
    try {
        const parsed =
            typeof raw === 'string'
                ? (JSON.parse(s) as Record<string, unknown>)
                : (raw as Record<string, unknown>);
        if (!parsed || typeof parsed !== 'object') {
            return null;
        }
        return normalizeRowForForm(parsed as EmployeePermissionClassRow);
    } catch {
        return null;
    }
}

/** 读取 `personal_permission_enabled`（0/1） */
export function readPersonalPermissionEnabled(raw: unknown): boolean {
    return Number(raw) === 1;
}

/** API 语义「全部关闭」的权限基线（`extensions: 1` 表示无扩展权限） */
export function buildAllOffPermissionApiBaseline(): EmployeePermissionClassRow {
    const row: EmployeePermissionClassRow = { payment: 0, extensions: 1 };
    for (const key of PERMISSION_ALL_KEYS) {
        if (key !== 'payment' && key !== 'extensions') {
            row[key] = 0;
        }
    }
    return row;
}

/** 岗位模板行；无匹配等级时用全关基线，便于空模板下配置个人权限例外 */
export function resolvePermissionTemplateBaseline(
    templateRow: EmployeePermissionClassRow | null,
): EmployeePermissionClassRow {
    return templateRow ?? buildAllOffPermissionApiBaseline();
}

/**
 * 按岗位 id 字符串在等级列表中查找模板行（兼容接口返回 number / string）。
 * @param roleValue - 岗位下拉当前值
 * @param classes - `get_employee_permission_setting` 列表
 */
export function findEmployeeClassTemplateByRoleValue(
    roleValue: string,
    classes: EmployeePermissionClassRow[],
): EmployeePermissionClassRow | null {
    if (roleValue === '' || roleValue == null) {
        return null;
    }
    const id = Number(roleValue);
    if (!Number.isFinite(id) || id <= 0) {
        return null;
    }
    return classes.find((c) => c.employee_class_id != null && Number(c.employee_class_id) === id) ?? null;
}

/**
 * 最终生效权限（API 语义）：关补充→模板；开补充无 override→模板；有 override→以 override 为准（缺键用模板补全）。
 * 无岗位模板且无 override 时返回 `null`（避免误展示全关基线权限）。
 */
export function resolveEffectivePermissionApiRow(
    personalSupplement: boolean,
    overrideForm: EmployeePermissionClassRow | null,
    templateRow: EmployeePermissionClassRow | null,
): EmployeePermissionClassRow | null {
    if (!templateRow && !overrideForm) {
        return null;
    }
    const baseline = resolvePermissionTemplateBaseline(templateRow);
    if (!personalSupplement || !overrideForm) {
        return templateRow ? _.cloneDeep(baseline) : null;
    }
    const apiTemplate = formPermissionRowToApiSnapshot(normalizeRowForForm(baseline));
    const apiOverride = formPermissionRowToApiSnapshot(overrideForm);
    return { ...baseline, ...apiTemplate, ...apiOverride } as EmployeePermissionClassRow;
}

/** 用于 Chip 展示：在生效行上筛出勾选为「开」的权限项定义 */
export function listEnabledPermissionDefsForChips(effectiveApiRow: EmployeePermissionClassRow | null | undefined) {
    if (!effectiveApiRow) {
        return [];
    }
    const normalized = normalizeRowForForm(effectiveApiRow);
    const defs = [...PERMISSION_PAYMENT_ITEMS, ...PERMISSION_MANAGER_ITEMS, ...PERMISSION_BACKSTAGE_ITEMS];
    return defs.filter((d) => Number(get(normalized, d.key, 0)) === 1);
}

/** 由模板与生效权限计算例外项列表 */
export function computeJobSettingsExceptions(
    personalSupplement: boolean,
    overrideForm: EmployeePermissionClassRow | null,
    templateRow: EmployeePermissionClassRow | null,
): JobSettingsExceptionItem[] {
    if (!personalSupplement) {
        return [];
    }
    if (!templateRow && !overrideForm) {
        return [];
    }
    const baseline = resolvePermissionTemplateBaseline(templateRow);
    const effective = resolveEffectivePermissionApiRow(personalSupplement, overrideForm, templateRow);
    if (!effective) {
        return [];
    }
    const items: JobSettingsExceptionItem[] = [];
    for (const key of PERMISSION_ALL_KEYS) {
        const def = PERMISSION_DEF_BY_KEY.get(key);
        if (!def) {
            continue;
        }
        const templateOn = normalizePermissionBitForCompare(key, get(baseline, key, 0));
        const effectiveOn = normalizePermissionBitForCompare(key, get(effective, key, 0));
        if (templateOn === effectiveOn) {
            continue;
        }
        const kind: JobSettingsExceptionKind = effectiveOn === 1 ? 'add' : 'subtract';
        items.push({
            id: key,
            kind,
            permissionKey: key,
            titleI18nKey: def.labelKey,
            descI18nKey:
                kind === 'add'
                    ? 'job_settings_exception_desc_force_allow'
                    : 'job_settings_exception_desc_force_deny',
        });
    }
    return items;
}

/** 表单态是否与模板一致（决定保存是否传 `permission_override`） */
export function isOverrideFormEqualToTemplate(
    overrideForm: EmployeePermissionClassRow | null,
    templateRow: EmployeePermissionClassRow | null,
): boolean {
    const templateForm = normalizeRowForForm(resolvePermissionTemplateBaseline(templateRow));
    if (!overrideForm) {
        return true;
    }
    for (const key of PERMISSION_ALL_KEYS) {
        const a = Number(get(overrideForm, key, 0)) === 1 ? 1 : 0;
        const b = Number(get(templateForm, key, 0)) === 1 ? 1 : 0;
        if (a !== b) {
            return false;
        }
    }
    return true;
}

/** 弹窗打开时的初始矩阵：当前生效权限的表单态 */
export function buildInitialOverrideFormForDialog(
    personalSupplement: boolean,
    overrideForm: EmployeePermissionClassRow | null,
    templateRow: EmployeePermissionClassRow | null,
): EmployeePermissionClassRow {
    const effective = resolveEffectivePermissionApiRow(personalSupplement, overrideForm, templateRow);
    if (!effective) {
        return normalizeRowForForm(buildAllOffPermissionApiBaseline());
    }
    return normalizeRowForForm(effective);
}

/** 将单权限位恢复为模板值；若与模板完全一致则返回 `null` */
export function revertPermissionKeyToTemplate(
    overrideForm: EmployeePermissionClassRow | null,
    templateRow: EmployeePermissionClassRow | null,
    permissionKey: string,
): EmployeePermissionClassRow | null {
    const templateForm = normalizeRowForForm(resolvePermissionTemplateBaseline(templateRow));
    const next = _.cloneDeep(overrideForm ?? templateForm);
    next[permissionKey] = templateForm[permissionKey];
    return isOverrideFormEqualToTemplate(next, templateRow) ? null : next;
}

/** 合并进员工 PATCH 正文的个人权限字段 */
export function buildPersonalPermissionPatchFields(
    personalSupplement: boolean,
    overrideForm: EmployeePermissionClassRow | null,
    templateRow: EmployeePermissionClassRow | null,
): Record<string, unknown> {
    const body: Record<string, unknown> = {
        personal_permission_enabled: personalSupplement ? 1 : 0,
    };
    if (!personalSupplement) {
        return body;
    }
    if (!overrideForm || isOverrideFormEqualToTemplate(overrideForm, templateRow)) {
        return body;
    }
    body.permission_override = JSON.stringify(formPermissionRowToApiSnapshot(overrideForm));
    return body;
}

/**
 * 将个人权限字段写入员工 PATCH 正文：未传 `permission_override` 时从 body 删除该键（避免 `...raw` 带入 `null`）。
 */
export function applyPersonalPermissionPatchToBody(
    body: Record<string, unknown>,
    personalSupplement: boolean,
    overrideForm: EmployeePermissionClassRow | null,
    templateRow: EmployeePermissionClassRow | null,
): void {
    const patch = buildPersonalPermissionPatchFields(personalSupplement, overrideForm, templateRow);
    Object.assign(body, patch);
    if (!Object.prototype.hasOwnProperty.call(patch, 'permission_override')) {
        delete body.permission_override;
    }
}
